import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Organization, OrganizationRole } from '@/types/database'
import { AuthContext, type AuthContextValue, type Membership } from './AuthContext'

/**
 * Shape of the membership + organization join below.
 *
 * The embedded resource is aliased to `organization` in the select. Without an
 * alias PostgREST names the key after the table, so renaming the table changes
 * the response shape and quietly breaks this mapping — which is exactly what
 * happened when the tables gained their matchmaker_ prefix.
 */
interface MembershipRow {
  role: OrganizationRole
  organization: Organization | null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [memberships, setMemberships] = useState<Membership[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadMemberships = useCallback(async (activeSession: Session | null) => {
    if (!activeSession) {
      setMemberships([])
      return
    }

    // RLS restricts this to the caller's own memberships, so no user filter is
    // needed here — and adding one would not be a security control anyway.
    const { data, error: queryError } = await supabase
      .from('matchmaker_organization_members')
      .select('role, organization:matchmaker_organizations(*)')
      .order('created_at', { ascending: true })

    if (queryError) {
      setError(queryError.message)
      setMemberships([])
      return
    }

    const rows = data as unknown as MembershipRow[]

    setMemberships(
      rows
        .filter((row): row is MembershipRow & { organization: Organization } => {
          return row.organization !== null
        })
        .map((row) => ({ role: row.role, organization: row.organization }))
    )
  }, [])

  useEffect(() => {
    let cancelled = false

    void supabase.auth
      .getSession()
      .then(async ({ data }) => {
        if (cancelled) return
        setSession(data.session)
        await loadMemberships(data.session)
      })
      .finally(() => {
        setLoading(false)
      })

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      void loadMemberships(nextSession)
    })

    return () => {
      cancelled = true
      listener.subscription.unsubscribe()
    }
  }, [loadMemberships])

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null)
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
    if (signInError) {
      // Deliberately generic: distinguishing "no such user" from "wrong
      // password" would let anyone enumerate admin accounts.
      setError('Email or password is incorrect.')
      throw signInError
    }
  }, [])

  const signOut = useCallback(async () => {
    setError(null)
    await supabase.auth.signOut()
    setMemberships([])
  }, [])

  const refreshMemberships = useCallback(async () => {
    await loadMemberships(session)
  }, [loadMemberships, session])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      memberships,
      loading,
      error,
      signIn,
      signOut,
      refreshMemberships,
    }),
    [session, memberships, loading, error, signIn, signOut, refreshMemberships]
  )

  return <AuthContext value={value}>{children}</AuthContext>
}
